// src/config/constants.ts

/**
 * Configuración de la API
 */
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL,
  TIMEOUT: 30000, // 30 segundos
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 segundo
} as const;

/**
 * Configuración de autenticación
 */
export const AUTH_CONFIG = {
  TOKEN_KEY: 'token',
  REFRESH_TOKEN_KEY: 'refresh_token',
  USER_KEY: 'user',
  ROLE_KEY: 'role',
  TOKEN_EXPIRATION_KEY: 'token_expiration',
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 horas en ms
  REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutos antes de expirar
} as const;

/**
 * Configuración de paginación
 */
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 1000,
} as const;

/**
 * Configuración de taquillas
 */
export const LOCKER_CONFIG = {
  DEFAULT_ASSIGNMENT_DURATION_HOURS: 24,
  MAX_ASSIGNMENT_DURATION_DAYS: 30,
  AUTO_RELEASE_HOURS: 48,
  MAX_LOCKERS_PER_ASSIGNMENT: 10,
  MAINTENANCE_CHECK_INTERVAL_DAYS: 30,
} as const;

/**
 * Estados de taquillas
 */
export const LOCKER_STATUS = {
  AVAILABLE: 'Available',
  OCCUPIED: 'Occupied',
  MAINTENANCE: 'Maintenance',
  OUT_OF_SERVICE: 'OutOfService',
  RESERVED: 'Reserved',
} as const;

/**
 * Estados de asignaciones
 */
export const ASSIGNMENT_STATUS = {
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
} as const;

/**
 * Tipos de alertas
 */
export const ALERT_TYPES = {
  MAINTENANCE: 'maintenance',
  SECURITY: 'security',
  AVAILABILITY: 'availability',
  SYSTEM: 'system',
} as const;

/**
 * Severidad de alertas
 */
export const ALERT_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

/**
 * Configuración del dashboard
 */
export const DASHBOARD_CONFIG = {
  REFRESH_INTERVAL: 30000, // 30 segundos
  CHART_ANIMATION_DURATION: 300,
  MAX_RECENT_ITEMS: 10,
  DEFAULT_PERIOD: 'today',
} as const;

/**
 * Configuración de notificaciones
 */
export const NOTIFICATION_CONFIG = {
  DEFAULT_DURATION: 5000, // 5 segundos
  ERROR_DURATION: 10000, // 10 segundos
  SUCCESS_DURATION: 3000, // 3 segundos
  MAX_NOTIFICATIONS: 3,
} as const;

/**
 * Configuración de validación
 */
export const VALIDATION_CONFIG = {
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  MIN_NAME_LENGTH: 2,
  MAX_NAME_LENGTH: 100,
  MAX_DESCRIPTION_LENGTH: 500,
  MAX_NOTES_LENGTH: 1000,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^[\d\s\-\+\(\)]+$/,
  LOCKER_NUMBER_REGEX: /^[A-Z0-9\-]+$/,
  HARDWARE_ID_REGEX: /^[A-F0-9]{8,}$/i,
  TAG_UID_REGEX: /^[A-F0-9]{8,16}$/i,
} as const;

/**
 * Configuración de archivos
 */
export const FILE_CONFIG = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'application/msword', 'application/vnd.ms-excel'],
  MAX_LOGO_SIZE: 2 * 1024 * 1024, // 2MB
} as const;

/**
 * Configuración de exportación
 */
export const EXPORT_CONFIG = {
  CSV_DELIMITER: ',',
  CSV_ENCODING: 'utf-8',
  DATE_FORMAT: 'YYYY-MM-DD',
  DATETIME_FORMAT: 'YYYY-MM-DD HH:mm:ss',
  DECIMAL_SEPARATOR: '.',
  THOUSANDS_SEPARATOR: ',',
} as const;

/**
 * Colores del tema
 */
export const THEME_COLORS = {
  PRIMARY: '#1976d2',
  SECONDARY: '#dc004e',
  SUCCESS: '#4caf50',
  WARNING: '#ff9800',
  ERROR: '#f44336',
  INFO: '#2196f3',
  BACKGROUND: {
    DEFAULT: '#fafafa',
    PAPER: '#ffffff',
  },
  TEXT: {
    PRIMARY: 'rgba(0, 0, 0, 0.87)',
    SECONDARY: 'rgba(0, 0, 0, 0.6)',
    DISABLED: 'rgba(0, 0, 0, 0.38)',
  },
} as const;

/**
 * Configuración de idiomas
 */
export const LANGUAGE_CONFIG = {
  DEFAULT_LANGUAGE: 'es',
  SUPPORTED_LANGUAGES: [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
  ],
} as const;

/**
 * Configuración de fechas
 */
export const DATE_CONFIG = {
  DEFAULT_FORMAT: 'DD/MM/YYYY',
  DISPLAY_FORMAT: 'DD MMM YYYY',
  INPUT_FORMAT: 'YYYY-MM-DD',
  TIME_FORMAT: 'HH:mm',
  DATETIME_FORMAT: 'DD/MM/YYYY HH:mm',
  LOCALE: 'es-ES',
} as const;

/**
 * Configuración de WebSocket (si se usa)
 */
export const WEBSOCKET_CONFIG = {
  URL: process.env.REACT_APP_WS_URL,
  RECONNECT_INTERVAL: 5000,
  MAX_RECONNECT_ATTEMPTS: 10,
  HEARTBEAT_INTERVAL: 30000,
} as const;

/**
 * Configuración de mapas (si se usa)
 */
export const MAP_CONFIG = {
  DEFAULT_CENTER: { lat: 40.4168, lng: -3.7038 }, // Madrid
  DEFAULT_ZOOM: 12,
  MIN_ZOOM: 5,
  MAX_ZOOM: 18,
} as const;

/**
 * Configuración de desarrollo
 */
export const DEV_CONFIG = {
  ENABLE_REDUX_DEVTOOLS: process.env.NODE_ENV === 'development',
  ENABLE_REACT_QUERY_DEVTOOLS: process.env.NODE_ENV === 'development',
  LOG_LEVEL: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
  MOCK_API: process.env.REACT_APP_MOCK_API === 'true',
} as const;

// Tipos derivados de las constantes
export type LockerStatusType = typeof LOCKER_STATUS[keyof typeof LOCKER_STATUS];
export type AssignmentStatusType = typeof ASSIGNMENT_STATUS[keyof typeof ASSIGNMENT_STATUS];
export type AlertTypeType = typeof ALERT_TYPES[keyof typeof ALERT_TYPES];
export type AlertSeverityType = typeof ALERT_SEVERITY[keyof typeof ALERT_SEVERITY];

// Helpers para obtener valores de las constantes
export const getLockerStatusValues = (): string[] => Object.values(LOCKER_STATUS);
export const getAssignmentStatusValues = (): string[] => Object.values(ASSIGNMENT_STATUS);
export const getAlertTypeValues = (): string[] => Object.values(ALERT_TYPES);
export const getAlertSeverityValues = (): string[] => Object.values(ALERT_SEVERITY);

// Validadores basados en constantes
export const isValidLockerStatus = (status: string): status is LockerStatusType => {
  return getLockerStatusValues().includes(status);
};

export const isValidAssignmentStatus = (status: string): status is AssignmentStatusType => {
  return getAssignmentStatusValues().includes(status);
};

export const isValidAlertType = (type: string): type is AlertTypeType => {
  return getAlertTypeValues().includes(type);
};

export const isValidAlertSeverity = (severity: string): severity is AlertSeverityType => {
  return getAlertSeverityValues().includes(severity);
};